import Editor from '@monaco-editor/react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Dropdown from '../components/Common/Dropdown';
import {
  setAllCodeingQuestions,
  setCurrentCodeingQuestionAnswer,
  setSelectedCodeingLanguage
} from '../redux/Slices/StartTestSlice/startFulTest';
import { useAppSelector } from '../redux/store/hooks';
type EditorPageProps = {
  questionNumber: number;
  questionText: string;
  questionId: string;
  sectionId: string;
  compileResult?: any;
  starterCode?: any;
  shouldShowStarterCode?: boolean;
};
const EditorPage = ({
  questionNumber,
  questionText,
  questionId,
  sectionId,
  compileResult,
  starterCode,
  shouldShowStarterCode
}: EditorPageProps) => {
  const [language, setLanguage] = useState([
    {
      label: 'JavaScript',
      value: 'javascript'
    },
    {
      label: 'Python',
      value: 'python'
    },
    {
      label: 'C++',
      value: 'cpp'
    },
    {
      label: 'Java',
      value: 'java'
    },
    {
      label: 'C',
      value: 'c'
    }
  ]);
  const [theme, setTheme] = useState('vs-dark');
  const dispatch = useDispatch();
  const {
    selectedCodeingLanguage,
    currentCodeingQuestionAnswer,
    allCodeingQuestions
  } = useAppSelector(state => state.root.startFulltest);
  const { written } = useAppSelector(state => state.root.questions);
  const currentQuestion = written[questionNumber];

  const defaultCode = {
    javascript: '', //`// Start coding here\n`
    python: '', // `# Start coding here\n`
    cpp: '', // Start coding here\n#include <iostream>\nusing namespace std;\n\nint main() {\n    \n    return 0;\n}
    java: '', // public class Main {\n    public static void main(String[] args) {\n        // Start coding here\n    }\n}
    c: '' // // Start coding here\n#include <stdio.h>\n\nint main() {\n    \n    return 0;\n}
  };

  const themes = [
    { value: 'vs-dark', label: 'Dark' },
    { value: 'light', label: 'Light' },
    { value: 'hc-black', label: 'High Contrast Dark' },
    { value: 'hc-light', label: 'High Contrast Light' }
  ];

  const handleLanguageChange = (e: { label: string; value: string }) => {
    const codingAnswer = shouldShowStarterCode
      ? starterCode?.find((code: any) => code?.language === e?.value)?.code ||
        ''
      : currentCodeingQuestionAnswer;
    const newLanguage = e?.value;
    dispatch(setSelectedCodeingLanguage(newLanguage));
    handleEditorChange(codingAnswer, newLanguage);
  };

  const handleEditorChange = (value: string | undefined, language?: string) => {
    dispatch(setCurrentCodeingQuestionAnswer(value));

    const currentCodeingQuestion = {
      questionId,
      sectionId,
      question: questionText,
      language: language || selectedCodeingLanguage,
      userProvidedSoluation: value
    };

    const filteredQuestions = allCodeingQuestions.filter(
      (q: { questionId: string }) => q.questionId !== questionId
    );

    const updatedQuestions = [...filteredQuestions, currentCodeingQuestion];

    dispatch(setAllCodeingQuestions(updatedQuestions));
  };

  const handleMonacoEditorChange = (value: string | undefined) => {
    handleEditorChange(value);
  };
  const getBgColor = (type: string) => {
    if (type === 'dropdown') {
      if (theme === 'vs-dark' || theme === 'hc-black') {
        return 'bg-transparent border-none text-white';
      }
      return 'bg-transparent border-none ';
    } else {
      if (theme === 'vs-dark' || theme === 'hc-black') {
        return 'bg-[#1e1e1e]';
      }
      return 'bg-transparent';
    }
  };

  useEffect(() => {
    const isAvailable = allCodeingQuestions?.find(
      (each: any) => each?.questionId === questionId
    );
    if (isAvailable) {
      handleEditorChange(isAvailable?.userProvidedSoluation);
    } else {
      const codingAnswer = shouldShowStarterCode
        ? starterCode?.find(
            (code: any) => code?.language === selectedCodeingLanguage
          )?.code
        : '';
      handleEditorChange(codingAnswer);
    }
  }, [questionNumber]);

  return (
    <div className='text-xl font-semibold '>
      <div className='flex flex-col'>
        <div
          className={`flex w-full items-center justify-end gap-4 border-b border-gray-400 ${getBgColor('content')}`}
        >
          <Dropdown
            options={language}
            value={
              language.find(each => each.value === selectedCodeingLanguage)
                ?.label || ''
            }
            handleDropdownChange={e => handleLanguageChange(e)}
            getOption={(val: any) => val.label}
            placeholder='Select Language'
            width='w-50'
            classes={getBgColor('dropdown')}
          />
          <Dropdown
            options={themes}
            value={themes.find(each => each.value === theme)?.label || ''}
            handleDropdownChange={e => setTheme(e.value)}
            getOption={(val: any) => val.label}
            placeholder='Select Theme'
            width='w-50'
            classes={getBgColor('dropdown')}
          />
        </div>
        <div
          className='w-full'
          style={{
            height: compileResult ? 'calc(45vh - 89px)' : 'calc(67vh - 89px)'
          }}
        >
          <Editor
            height='100%'
            language={selectedCodeingLanguage}
            value={currentCodeingQuestionAnswer}
            theme={theme}
            onChange={handleMonacoEditorChange}
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              automaticLayout: true,
              renderLineHighlight: 'none'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default EditorPage;
